/**
 * Gast-Leads-Kachel (Feb 2026).
 *
 * Misst den Geschäftswert der reibungslosen Gast-Anfragen: wie viele Gast-Konten
 * (passwortlos, ``auth_provider="guest"``) diesen Händler kontaktiert haben
 * (Nachricht ODER Preisvorschlag) und wie viele davon zu dauerhaft
 * registrierten Käufern konvertiert sind (Passwort gesetzt). Wird nur angezeigt,
 * wenn mindestens ein Gast-Lead vorliegt (sonst kein Clutter).
 *
 * Datenquelle: ``GET /api/dealer/guest-leads-stats``.
 */
import { useEffect, useState } from "react";
import { UserPlus, CheckCircle2, TrendingUp, ArrowRight } from "lucide-react";
import api from "../../lib/api";

export default function GuestLeadsCard({ user, onOpenGuestInbox }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!user || user.role !== "b2b") return;
        api.get("/dealer/guest-leads-stats")
            .then((r) => setData(r.data))
            .catch(() => setData(null));
    }, [user]);

    if (!user || user.role !== "b2b") return null;
    if (!data) return null;
    if ((data.total || 0) === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="guest-leads-empty-hint">
                <div className="flex items-center gap-1.5 mb-1">
                    <UserPlus className="w-3.5 h-3.5 text-amber-500" />
                    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600">Gast-Leads</div>
                </div>
                <div className="text-[11px] font-bold text-gray-700 mb-1">So funktioniert&apos;s</div>
                <p className="text-[11px] text-gray-500 leading-relaxed">
                    Interessenten können Sie <strong>ohne Registrierung</strong> kontaktieren (Nachricht oder Preisvorschlag) — sie erhalten automatisch ein Gast-Konto. Solche Anfragen erscheinen hier und in Ihrem Gast-Postfach. Je mehr aktive Inserate, desto mehr Gast-Anfragen.
                </p>
                <button
                    type="button"
                    onClick={onOpenGuestInbox}
                    className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 font-bold text-[11px] uppercase tracking-wider rounded-sm transition-colors"
                    data-testid="guest-leads-inbox-btn"
                >
                    Gast-Postfach öffnen <ArrowRight className="w-3 h-3" />
                </button>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={onOpenGuestInbox}
            className="w-full text-left bg-white border border-gray-200 rounded-sm p-3 hover:border-amber-400 hover:shadow-sm transition-colors group"
            data-testid="guest-leads-card"
            title="Gast-Anfragen im Postfach öffnen"
        >
            <div className="flex items-center gap-1.5 mb-2">
                <UserPlus className="w-3.5 h-3.5 text-amber-500" />
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600">
                    Gast-Leads
                </div>
                {(data.recent || 0) > 0 && (
                    <span
                        className="bg-amber-500 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                        data-testid="guest-leads-recent"
                        title="Neue Gast-Anfragen in den letzten 24 Stunden"
                    >
                        +{data.recent} neu
                    </span>
                )}
                <ArrowRight className="w-3 h-3 text-gray-400 ml-auto group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div className="bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 text-center" data-testid="guest-leads-total">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-gray-600 flex items-center justify-center gap-1">
                        <UserPlus className="w-2.5 h-2.5" /> Anfragen
                    </div>
                    <div className="font-display font-black text-base font-mono-num leading-tight">{data.total}</div>
                    <div className="text-[8px] text-gray-500">ohne Login</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5 text-center" data-testid="guest-leads-converted">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-gray-600 flex items-center justify-center gap-1">
                        <CheckCircle2 className="w-2.5 h-2.5" /> Konto
                    </div>
                    <div className="font-display font-black text-base font-mono-num leading-tight">{data.converted}</div>
                    <div className="text-[8px] text-gray-500">registriert</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-sm px-2 py-1.5 text-center" data-testid="guest-leads-conversion">
                    <div className="text-[9px] uppercase font-bold tracking-wider text-gray-600 flex items-center justify-center gap-1">
                        <TrendingUp className="w-2.5 h-2.5" /> Quote
                    </div>
                    <div className="font-display font-black text-base font-mono-num leading-tight" data-testid="guest-leads-conversion-value">
                        {data.conversion_pct}%
                    </div>
                    <div className="text-[8px] text-gray-500">konvertiert</div>
                </div>
            </div>
        </button>
    );
}
